import { openDB } from 'idb';

const DB_NAME = 'ChallengeImageCache';
const STORE_NAME = 'images';
const DB_VERSION = 1;
const EXPIRATION_TIME = 2 * 24 * 60 * 60 * 1000; // 2 days in milliseconds

type CachedImage = {
  challenge_uid: string;  // used as the key
  image_uid: string;
  blob: Blob;
  timestamp: number;
};

export const getImageDB = async () => {
  return openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'challenge_uid' });
      }
    },
  });
};

export const cacheImage = async (
  challenge_uid: string,
  image_uid: string,
  base64: string,
  timestamp?: number
) => {
  await cleanupExpiredImages();
  const db = await getImageDB();
  const blob = base64ToBlob(base64);
  const entry: CachedImage = {
    challenge_uid,
    image_uid,
    blob,
    timestamp: timestamp || Date.now(),
  };
  
  // Delete old cache entry if exists (to handle updated images)
  try {
    const existingEntry = await db.get(STORE_NAME, challenge_uid);
    if (existingEntry) {
      await db.delete(STORE_NAME, challenge_uid);
    }
  } catch (err) {
    console.warn('Error checking existing cache:', err);
  }
  
  await db.put(STORE_NAME, entry);
};

export const getCachedImage = async (
  challenge_uid: string
): Promise<CachedImage | null> => {
  const db = await getImageDB();
  const entry = await db.get(STORE_NAME, challenge_uid) as CachedImage | undefined;

  if (!entry) return null;

  const now = Date.now();
  if (now - entry.timestamp > EXPIRATION_TIME) {
    await db.delete(STORE_NAME, challenge_uid); // delete expired image
    return null;
  }

  return entry;
};

const cleanupExpiredImages = async () => {
  const db = await getImageDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.store;
  const now = Date.now();

  let cursor = await store.openCursor();
  while (cursor) {
    const entry = cursor.value as CachedImage;
    if (now - entry.timestamp > EXPIRATION_TIME) {
      await cursor.delete();
    }
    cursor = await cursor.continue();
  }

  await tx.done;
};

function base64ToBlob(base64Data: string): Blob {
  // Handle both raw base64 and data URLs
  const base64 = base64Data.includes('base64,') 
    ? base64Data.split('base64,')[1] 
    : base64Data;
    
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: 'image/jpeg' });
}

// New utility function to revoke object URLs
export const revokeObjectURL = (url: string) => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};